import { Component } from '@theme/component';

/**
 * <admira-solar-calculator> — plan §7.8 + §7.9, structure configurator and
 * PV-system calculator as one tool; the section's comment has the design.
 * Formulas and the cart call live in assets/admira-calculator-core.js, loaded
 * by URL so the vendor import map stays unforked (and this file's budget —
 * AssetSizeJavaScript — stays clear).
 *
 * State is plain data; every change re-derives the whole view from it. There
 * is no "generate" step. The layout is edited by drag and drop, by tap (pick a
 * panel, tap a row), or by arrow keys; the first move makes it custom.
 *
 * @extends {Component<{
 *   form: HTMLFormElement, canvas: HTMLElement, announcer: HTMLElement,
 *   statRows: HTMLElement, statPanels: HTMLElement, reset: HTMLButtonElement,
 *   length: HTMLInputElement, lengthOutput: HTMLOutputElement, lengthMin: HTMLElement, lengthMax: HTMLElement,
 *   minirailSelect: HTMLSelectElement, panelEstimate: HTMLElement,
 *   consumptionRange: HTMLInputElement, consumptionOutput: HTMLOutputElement, consumptionSummary: HTMLElement,
 *   powers: HTMLElement, preview: HTMLElement, notice: HTMLElement, payback: HTMLElement,
 *   costs: HTMLElement, systemTotal: HTMLElement, ratesFrom?: HTMLElement,
 *   tiles: HTMLElement, rows: HTMLElement, count: HTMLElement, summary: HTMLElement, total: HTMLElement, status: HTMLElement,
 *   credit?: HTMLInputElement, term?: HTMLSelectElement, rate?: HTMLElement,
 *   plan?: HTMLDialogElement, planOpen?: HTMLButtonElement, planClose?: HTMLButtonElement, planBody?: HTMLElement,
 * }>}
 */
class AdmiraSolarCalculator extends Component {
  requiredRefs = ['form', 'canvas', 'tiles', 'rows', 'total'];

  /** @type {typeof import('./admira-calculator-core.js')} */
  #core;
  #strings = {};
  #structure;
  #system;

  #state = {
    /** 'structure' | 'system' */
    kind: 'structure',
    panels: 12,
    rows: 3,
    orientation: 'portrait',
    roof: 'tile',
    panelLength: 1135,
    ribSpacing: 500,
    minirailPoints: 4,
    minirailKey: '',
    custom: false,
    /** @type {number[][]} panel numbers per row */
    layout: [],
    mode: 'consumption',
    consumption: 350,
    powerKw: 6,
    phases: 'three',
    system: 'hybrid',
  };

  /** @type {Map<string, number>} user-edited quantities, by component key */
  #overrides = new Map();
  /** @type {import('./admira-calculator-core.js').Line[]} inverter and battery first, then the structure */
  #lines = [];
  /** @type {{ target: number, selectedKw: number, inverter: any, battery: any, modules: number } | null} */
  #sizing = null;
  /** @type {number | null} panel picked up by tap, awaiting a destination */
  #picked = null;
  /** @type {number | null} */
  #dragged = null;
  /** The panel count was set by hand, so consumption no longer estimates it. */
  #panelsTouched = false;

  async connectedCallback() {
    super.connectedCallback();

    this.#core = await import(/* @vite-ignore */ this.dataset.core ?? '');
    this.#strings = JSON.parse(this.querySelector('[data-calculator-strings]')?.textContent || '{}');
    this.#structure = this.#core.readCatalog(this, 'structure');
    this.#system = this.#core.readCatalog(this, 'system');

    this.#fillMinirailSelect();
    this.#applyKindFromUrl();
    this.#readForm();
    if (this.#state.kind === 'system') this.#syncPanelEstimate();
    this.#autoLayout();

    const { form, canvas, reset, rows, credit, term, plan, planOpen, planClose } = this.refs;
    form.addEventListener('input', this.#onFormChange);
    form.addEventListener('change', this.#onFormChange);
    form.addEventListener('submit', (event) => event.preventDefault());
    this.addEventListener('click', this.#onStep);
    reset?.addEventListener('click', this.#onReset);

    canvas.addEventListener('click', this.#onCanvasClick);
    canvas.addEventListener('keydown', this.#onCanvasKey);
    canvas.addEventListener('dragstart', this.#onDragStart);
    canvas.addEventListener('dragover', this.#onDragOver);
    canvas.addEventListener('dragleave', this.#onDragLeave);
    canvas.addEventListener('drop', this.#onDrop);
    canvas.addEventListener('dragend', this.#onDragEnd);

    rows.addEventListener('input', this.#onLineQuantity);
    rows.addEventListener('change', this.#onLineQuantity);
    rows.addEventListener('click', this.#onLineAdd);
    for (const button of this.querySelectorAll('[data-add-all]')) {
      button.addEventListener('click', this.#onAddAll);
    }

    credit?.addEventListener('input', this.#renderTbi);
    term?.addEventListener('change', this.#renderTbi);
    planOpen?.addEventListener('click', () => {
      this.#renderPlan();
      plan?.showModal();
    });
    planClose?.addEventListener('click', () => plan?.close());
    // A backdrop click targets the dialog itself, as does one on its padding, so compare against its box.
    plan?.addEventListener('click', (event) => {
      if (event.target !== plan) return;
      const box = plan.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) plan.close();
    });

    this.#render();
  }

  /* ------------------------------------------------------------------ */
  /* Form                                                               */
  /* ------------------------------------------------------------------ */

  /** `?tip=sistem` / `?tip=structura` opens that calculator; the section setting is the default. */
  #applyKindFromUrl() {
    const wanted = { sistem: 'system', structura: 'structure' }[new URL(window.location.href).searchParams.get('tip') ?? ''];
    const kind = wanted ?? this.dataset.defaultKind ?? 'structure';
    const radio = this.refs.form.querySelector(`input[name="calculator"][value="${kind}"]`);
    if (radio instanceof HTMLInputElement) radio.checked = true;
  }

  #syncUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('tip', this.#state.kind === 'system' ? 'sistem' : 'structura');
    history.replaceState(history.state, '', url);
  }

  /** Stepper buttons change their input and let its events do the rest. */
  #onStep = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-step]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const input = button.closest('[data-stepper]')?.querySelector('input');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = String(Number(input.value || 0) + Number(button.dataset.step));
    this.#core.clampInput(input);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #onFormChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      if (target.type === 'number' && event.type === 'change') this.#core.clampInput(target);
      // The consumption slider and its number field are one value.
      if (target.name === 'consumption_range') this.#field('consumption').value = target.value;
      if (target.name === 'consumption') this.refs.consumptionRange.value = target.value;
      if (target.name === 'panels') this.#panelsTouched = true;
    }

    const before = { ...this.#state };
    this.#readForm();
    const state = this.#state;

    if (state.kind !== before.kind) this.#syncUrl();

    // A new size for the system re-estimates the panels; switching to the
    // complete system does too, unless the count was already set by hand.
    const sizingChanged =
      state.mode !== before.mode || state.consumption !== before.consumption || state.powerKw !== before.powerKw ||
      state.system !== before.system || state.phases !== before.phases;
    if (state.kind === 'system') {
      if (sizingChanged) this.#panelsTouched = false;
      if (sizingChanged || state.kind !== before.kind) this.#syncPanelEstimate();
    }

    if (state.orientation !== before.orientation) this.#applyOrientation();

    if (state.rows !== before.rows) {
      // A row count is a request for the automatic layout over that many rows.
      state.custom = false;
      this.#autoLayout();
    } else if (state.panels !== before.panels) {
      if (state.custom) this.#fitLayoutToCount();
      else this.#autoLayout();
    }

    this.#overrides.clear();
    this.#render();
  };

  #readForm() {
    const data = new FormData(this.refs.form);
    const number = (name, fallback) => {
      const value = parseFloat(String(data.get(name) ?? ''));
      return Number.isFinite(value) ? value : fallback;
    };

    Object.assign(this.#state, {
      kind: data.get('calculator') === 'system' ? 'system' : 'structure',
      panels: Math.max(1, Math.round(number('panels', this.#state.panels))),
      rows: Math.max(1, Math.round(number('rows', this.#state.rows))),
      orientation: String(data.get('orientation') || 'portrait'),
      roof: String(data.get('roof') || 'tile'),
      panelLength: number('panel_length', this.#state.panelLength),
      ribSpacing: number('rib_spacing', 500),
      minirailPoints: number('minirail_points', 4),
      minirailKey: String(data.get('minirail') || ''),
      mode: String(data.get('mode') || 'consumption'),
      consumption: Math.max(0, number('consumption', this.#state.consumption)),
      powerKw: number('power', this.#state.powerKw),
      phases: String(data.get('phases') || 'three'),
      system: String(data.get('system') || 'hybrid'),
    });

    // Power mode offers exactly the inverter sizes the catalogue has for this
    // configuration; a size it no longer has moves to the nearest one up.
    const sizes = this.#powerSizes();
    if (sizes.length && !sizes.includes(this.#state.powerKw)) {
      this.#state.powerKw = sizes.find((kw) => kw >= this.#state.powerKw) ?? sizes[sizes.length - 1];
    }
  }

  /** @param {string} name */
  #field(name) {
    return /** @type {HTMLInputElement} */ (this.refs.form.elements.namedItem(name));
  }

  /** Keystone's slider bounds: 990-1300 portrait, 1650-2400 landscape. */
  #applyOrientation() {
    const { length, lengthMin, lengthMax } = this.refs;
    const landscape = this.#state.orientation === 'landscape';
    const [min, max, fallback] = landscape ? [1650, 2400, 1960] : [990, 1300, 1135];

    length.min = String(min);
    length.max = String(max);
    const current = Number(length.value);
    if (current < min || current > max) length.value = String(fallback);
    this.#state.panelLength = Number(length.value);

    if (lengthMin) lengthMin.textContent = this.#mm(min);
    if (lengthMax) lengthMax.textContent = this.#mm(max);
  }

  #fillMinirailSelect() {
    const select = this.refs.minirailSelect;
    if (!select) return;
    for (const entry of this.#structure.entries.filter((e) => e.role === 'minirail')) {
      select.append(new Option(entry.label || entry.title || entry.key, entry.key));
    }
  }

  /* ------------------------------------------------------------------ */
  /* System sizing — Keystone, verbatim                                 */
  /* ------------------------------------------------------------------ */

  #configuredKw() {
    const s = this.#state;
    return s.mode === 'power' ? Math.max(1, s.powerKw) : this.#core.targetKw(s.consumption);
  }

  #inverters() {
    const { system, phases } = this.#state;
    return this.#system.entries
      .filter((e) => e.role === 'inverter' && e.system_type === system && e.phases === phases)
      .sort((a, b) => a.power_kw - b.power_kw);
  }

  #powerSizes() {
    return this.#system ? [...new Set(this.#inverters().map((e) => e.power_kw))] : [];
  }

  #size() {
    const s = this.#state;
    const target = this.#configuredKw();
    const inverter = this.#core.pickInverter(this.#inverters(), target);
    const modules = this.#core.batteryModules({ system: s.system, mode: s.mode, monthlyKwh: s.consumption, kw: target });
    const battery = s.system === 'hybrid'
      ? this.#system.entries.find((e) => e.role === 'battery' && e.system_type === 'hybrid' && e.phases === s.phases) ?? null
      : null;
    return { target, selectedKw: inverter?.power_kw ?? target, inverter, battery, modules };
  }

  /** Panels estimated at 450 W each, written into the stepper so the layout follows. */
  #syncPanelEstimate() {
    if (this.#panelsTouched) return;
    const panels = this.#field('panels');
    const rows = this.#field('rows');
    panels.value = String(Math.min(Number(panels.max) || 50, this.#core.estimatedPanels(this.#configuredKw())));
    if (Number(rows.value) > Number(panels.value)) rows.value = panels.value;
    this.#state.panels = Number(panels.value);
    this.#state.rows = Number(rows.value);
  }

  /* ------------------------------------------------------------------ */
  /* Layout                                                             */
  /* ------------------------------------------------------------------ */

  #autoLayout() {
    const counts = this.#core.distribute(this.#state.panels, this.#state.rows);
    let next = 1;
    this.#state.layout = counts.map((count) => Array.from({ length: count }, () => next++));
  }

  /** Custom layout, panel count changed: drop panels over the count, add new ones to the shortest row. */
  #fitLayoutToCount() {
    const total = this.#state.panels;
    const layout = this.#state.layout.map((row) => row.filter((id) => id <= total)).filter((row) => row.length);
    const placed = new Set(layout.flat());

    for (let id = 1; id <= total; id++) {
      if (placed.has(id)) continue;
      if (!layout.length) layout.push([]);
      const shortest = layout.reduce((a, b) => (b.length < a.length ? b : a));
      shortest.push(id);
    }
    this.#state.layout = layout;
  }

  #onReset = () => {
    this.#state.custom = false;
    this.#autoLayout();
    this.#overrides.clear();
    this.#render();
  };

  /**
   * @param {number} id
   * @param {number} toRow - index; `layout.length` means a new row
   * @param {number} [toIndex] - position in that row; end when omitted
   */
  #move(id, toRow, toIndex) {
    const maxRows = Number(this.#field('rows')?.max) || 10;
    const layout = this.#state.layout.map((row) => row.filter((panel) => panel !== id));
    if (toRow >= layout.length) {
      if (layout.filter((row) => row.length).length >= maxRows) return;
      layout.push([]);
      toRow = layout.length - 1;
    }
    const row = layout[toRow];
    row.splice(toIndex ?? row.length, 0, id);

    // Collapse emptied rows, and keep the index of the destination row.
    const before = layout.slice(0, toRow).filter((r) => r.length === 0).length;
    this.#state.layout = layout.filter((r) => r.length);
    // The rows stepper keeps the automatic layout's count, which "Resetează
    // layout" returns to; "Rânduri active" reports the layout as it is.
    this.#state.custom = true;
    this.#overrides.clear();
    this.#render();

    this.#announce(this.#t('moved', { PANEL: id, ROW: toRow - before + 1 }));
    /** @type {HTMLElement | null} */ (this.refs.canvas.querySelector(`[data-panel="${id}"]`))?.focus();
  }

  #onCanvasClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const panel = event.target.closest('[data-panel]');
    if (panel instanceof HTMLElement) {
      const id = Number(panel.dataset.panel);
      this.#picked = this.#picked === id ? null : id;
      this.#renderCanvas();
      /** @type {HTMLElement | null} */ (this.refs.canvas.querySelector(`[data-panel="${id}"]`))?.focus();
      return;
    }

    if (this.#picked === null) return;
    const target = event.target.closest('[data-row], [data-new-row]');
    if (!(target instanceof HTMLElement)) return;
    const id = this.#picked;
    this.#picked = null;
    this.#move(id, target.dataset.newRow !== undefined ? this.#state.layout.length : Number(target.dataset.row));
  };

  #onCanvasKey = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const id = Number(event.target.dataset.panel);
    if (!id) return;

    const layout = this.#state.layout;
    const rowIndex = layout.findIndex((row) => row.includes(id));
    const index = layout[rowIndex].indexOf(id);

    switch (event.key) {
      case 'ArrowUp':
        if (rowIndex > 0) this.#move(id, rowIndex - 1);
        break;
      case 'ArrowDown':
        this.#move(id, rowIndex + 1);
        break;
      case 'ArrowLeft':
        if (index > 0) this.#move(id, rowIndex, index - 1);
        break;
      case 'ArrowRight':
        if (index < layout[rowIndex].length - 1) this.#move(id, rowIndex, index + 1);
        break;
      case 'Escape':
        this.#picked = null;
        this.#renderCanvas();
        return;
      default:
        return;
    }
    event.preventDefault();
  };

  #onDragStart = (event) => {
    const panel = event.target instanceof HTMLElement ? event.target.closest('[data-panel]') : null;
    if (!(panel instanceof HTMLElement)) return;
    this.#dragged = Number(panel.dataset.panel);
    event.dataTransfer?.setData('text/plain', panel.dataset.panel ?? '');
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    this.refs.canvas.dataset.dragging = 'true';
  };

  #onDragOver = (event) => {
    if (this.#dragged === null || !(event.target instanceof Element)) return;
    const target = event.target.closest('[data-row], [data-new-row]');
    if (!(target instanceof HTMLElement)) return;
    event.preventDefault();
    this.#clearDropTargets(target);
    target.dataset.dropTarget = 'true';
  };

  #onDragLeave = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('[data-row], [data-new-row]');
    // Moving between a row's own panels and slot is not leaving the row.
    if (target instanceof HTMLElement && !(event.relatedTarget instanceof Node && target.contains(event.relatedTarget))) {
      delete target.dataset.dropTarget;
    }
  };

  #onDragEnd = () => {
    this.#dragged = null;
    delete this.refs.canvas.dataset.dragging;
    this.#clearDropTargets();
  };

  /** @param {HTMLElement} [keep] */
  #clearDropTargets(keep) {
    for (const element of this.refs.canvas.querySelectorAll('[data-drop-target]')) {
      if (element !== keep && element instanceof HTMLElement) delete element.dataset.dropTarget;
    }
  }

  #onDrop = (event) => {
    if (this.#dragged === null || !(event.target instanceof Element)) return;
    event.preventDefault();
    const id = this.#dragged;
    this.#onDragEnd();

    const panel = event.target.closest('[data-panel]');
    const target = event.target.closest('[data-row], [data-new-row]');
    if (!(target instanceof HTMLElement)) return;

    if (target.dataset.newRow !== undefined) {
      this.#move(id, this.#state.layout.length);
      return;
    }
    // On a panel: take its place. On the row or its end slot: go to the end.
    const rowIndex = Number(target.dataset.row);
    const index = panel instanceof HTMLElement ? this.#state.layout[rowIndex].indexOf(Number(panel.dataset.panel)) : undefined;
    this.#move(id, rowIndex, index === -1 ? undefined : index);
  };

  /* ------------------------------------------------------------------ */
  /* Derivation                                                         */
  /* ------------------------------------------------------------------ */

  #materials() {
    const s = this.#state;
    return this.#core.structureMaterials({
      panelsPerRow: s.layout.map((row) => row.length),
      panelLengthMm: s.panelLength,
      roofType: s.roof,
      ribSpacingMm: s.ribSpacing,
      miniRailPoints: s.minirailPoints,
    });
  }

  #deriveLines(materials) {
    const core = this.#core;
    const lines = [];
    if (this.#sizing) {
      const { inverter, battery, modules } = this.#sizing;
      if (inverter) lines.push(core.toLine(inverter, 1, 'inverter'));
      if (battery && modules > 0) lines.push(core.toLine(battery, modules, 'battery'));
    }
    lines.push(...core.structureLines(materials, this.#structure, { roofType: this.#state.roof, minirailKey: this.#state.minirailKey }));

    for (const line of lines) {
      const override = line.entry ? this.#overrides.get(line.entry.key) : undefined;
      if (override !== undefined) line.quantity = override;
    }
    this.#lines = lines;
  }

  #sum(lines = this.#lines) {
    return lines.reduce((acc, line) => acc + this.#core.lineTotal(line), 0);
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                          */
  /* ------------------------------------------------------------------ */

  #render() {
    const s = this.#state;
    const system = s.kind === 'system';

    for (const element of this.querySelectorAll('[data-calculator-kind]')) {
      if (element instanceof HTMLElement) element.hidden = element.dataset.calculatorKind !== s.kind;
    }
    for (const field of this.querySelectorAll('[data-mode]')) {
      if (field instanceof HTMLElement) field.hidden = field.dataset.mode !== s.mode;
    }
    for (const field of this.querySelectorAll('[data-show-for]')) {
      if (field instanceof HTMLElement) field.hidden = !field.dataset.showFor?.split(' ').includes(s.roof);
    }
    for (const field of this.querySelectorAll('[data-hide-for]')) {
      if (field instanceof HTMLElement) field.hidden = field.dataset.hideFor?.split(' ').includes(s.roof) ?? false;
    }
    for (const button of this.querySelectorAll('[data-stepper] [data-step]')) {
      const input = button.closest('[data-stepper]')?.querySelector('input');
      if (!(button instanceof HTMLButtonElement) || !(input instanceof HTMLInputElement)) continue;
      const value = Number(input.value);
      button.disabled = Number(button.dataset.step) < 0 ? value <= Number(input.min) : value >= Number(input.max);
    }
    if (this.refs.lengthOutput) this.refs.lengthOutput.textContent = this.#mm(s.panelLength);

    this.#sizing = system ? this.#size() : null;
    const materials = this.#materials();
    this.#deriveLines(materials);

    this.#renderCanvas();
    if (system) {
      this.#renderSystemInputs();
      this.#renderRecommendation();
    }
    this.#renderList();
    this.#renderTiles(materials);
    if (system) {
      this.#renderTbi();
      this.#prefillFinance();
    }
  }

  #renderCanvas() {
    const { canvas, statRows, statPanels, reset } = this.refs;
    const { layout, custom, panels, orientation } = this.#state;

    canvas.toggleAttribute('data-landscape', orientation === 'landscape');
    canvas.toggleAttribute('data-picking', this.#picked !== null);
    canvas.replaceChildren();

    layout.forEach((row, rowIndex) => {
      const rowElement = document.createElement('div');
      rowElement.className = 'structure-calculator__panel-row';
      rowElement.dataset.row = String(rowIndex);
      rowElement.setAttribute('role', 'group');
      rowElement.setAttribute('aria-label', this.#t('row', { N: rowIndex + 1 }));

      for (const id of row) {
        const panel = document.createElement('button');
        panel.type = 'button';
        panel.className = 'structure-calculator__panel';
        panel.dataset.panel = String(id);
        panel.textContent = String(id);
        panel.draggable = true;
        panel.setAttribute('aria-label', `${this.#t('panel', { N: id })}, ${this.#t('row', { N: rowIndex + 1 })}`);
        panel.setAttribute('aria-pressed', String(this.#picked === id));
        if (this.#picked === id) panel.setAttribute('aria-grabbed', 'true');
        rowElement.append(panel);
      }

      // The dashed end slot: a visible "this row takes more panels". The row
      // itself is the drop and tap target, so the slot needs no semantics.
      const slot = document.createElement('span');
      slot.className = 'structure-calculator__slot';
      slot.setAttribute('aria-hidden', 'true');
      slot.textContent = '+';
      rowElement.append(slot);

      canvas.append(rowElement);
    });

    const newRow = document.createElement('button');
    newRow.type = 'button';
    newRow.className = 'structure-calculator__new-row';
    newRow.dataset.newRow = '';
    newRow.innerHTML = '<span aria-hidden="true">+</span>';
    newRow.append(document.createTextNode(this.#strings.new_row ?? ''));
    canvas.append(newRow);

    const placed = layout.reduce((sum, row) => sum + row.length, 0);
    if (statRows) statRows.textContent = String(layout.length);
    if (statPanels) statPanels.textContent = `${placed} / ${panels}`;
    if (reset) reset.disabled = !custom;
  }

  #renderSystemInputs() {
    const s = this.#state;
    const t = this.#strings;
    const { consumptionOutput, consumptionSummary, panelEstimate, powers } = this.refs;

    if (consumptionOutput) consumptionOutput.textContent = this.#fill(t.kwh_month, { N: s.consumption });
    if (consumptionSummary) {
      consumptionSummary.textContent = this.#fill(t.consumption_summary, { KWH: Math.round(s.consumption), KW: this.#core.targetKw(s.consumption) });
    }

    if (powers) {
      const sizes = this.#powerSizes();
      // Rebuilt only when the available sizes change, so arrowing through the
      // radio group never loses focus to a re-render.
      const key = sizes.join(',');
      if (powers.dataset.sizes === key) {
        for (const input of powers.querySelectorAll('input')) input.checked = Number(input.value) === s.powerKw;
      } else {
        powers.dataset.sizes = key;
        powers.replaceChildren(
          ...sizes.map((kw) => {
            const label = document.createElement('label');
            label.className = 'calculator-option calculator-option--compact';
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = 'power';
            input.value = String(kw);
            input.checked = kw === s.powerKw;
            const text = document.createElement('span');
            text.className = 'calculator-option__label';
            text.textContent = this.#fill(t.kw, { N: kw });
            label.append(input, text);
            return label;
          })
        );
      }
    }

    if (panelEstimate) {
      const kw = this.#configuredKw();
      panelEstimate.textContent = this.#fill(t.panel_estimate, { KW: kw, N: this.#core.estimatedPanels(kw) });
    }
  }

  #renderRecommendation() {
    const core = this.#core;
    const t = this.#strings;
    const s = this.#state;
    const sizing = this.#sizing;
    if (!sizing) return;
    const { preview, notice, payback, costs, systemTotal } = this.refs;

    const batteryText = s.system === 'hybrid' ? this.#fill(t.preview_battery, { COUNT: sizing.modules }) : t.preview_no_battery;
    preview.textContent = `${this.#fill(t.preview, { TARGET: sizing.target, SELECTED: sizing.selectedKw })} ${batteryText}`;

    const notices = [];
    if (s.phases === 'single' && sizing.target > 6) notices.push(t.notice_single_limit);
    if (sizing.inverter && sizing.selectedKw < sizing.target) notices.push(t.notice_catalog_limit);
    if (notice) {
      notice.textContent = notices.join(' ');
      notice.hidden = notices.length === 0;
    }

    const total = this.#sum();
    const years = this.#paybackYears(total);
    if (payback) {
      payback.textContent = years
        ? this.#fill(t.years?.[new Intl.PluralRules('ro').select(years)] ?? t.years?.other, { N: years })
        : t.payback_unavailable;
    }

    const byRole = (roles) => this.#sum(this.#lines.filter((line) => roles.includes(line.role)));
    const parts = [
      ['inverter', byRole(['inverter'])],
      ...(s.system === 'hybrid' ? [['battery', byRole(['battery'])]] : []),
      ['structure', this.#sum(this.#lines.filter((line) => line.role !== 'inverter' && line.role !== 'battery'))],
    ];
    costs?.replaceChildren(
      ...parts.map(([kind, cents]) => {
        const row = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = t[`category_${kind}`] ?? kind;
        dd.textContent = core.money(Number(cents), this);
        row.append(dt, dd);
        return row;
      })
    );
    if (systemTotal) systemTotal.textContent = core.money(total, this);
  }

  #renderTiles(materials) {
    const { tiles } = this.refs;
    const s = this.#strings;
    const roofName = s.roof?.[this.#state.roof] ?? '';
    const order = this.#state.roof === 'minirail'
      ? [['minirail', 'miniRails'], ['end_clamp', 'endClamps'], ['mid_clamp', 'middleClamps'], ['self_tapping_screw', 'selfTappingScrews']]
      : [['rail', 'profiles'], ['end_clamp', 'endClamps'], ['mid_clamp', 'middleClamps'], ['connector', 'connectors'], ['end_cap', 'plasticCaps'],
         ['grounding_clamp', 'groundingClamps'], ['mounting', 'mountingElements'], ['hex_screw', 'hexScrews'], ['flange_nut', 'flangeNuts'], ['wood_screw', 'woodScrews']];

    const tile = (labelText, valueText, unitText) => {
      const item = document.createElement('li');
      item.className = 'structure-calculator__tile';
      const label = document.createElement('p');
      label.className = 'structure-calculator__tile-label';
      label.textContent = labelText;
      const value = document.createElement('p');
      value.className = 'structure-calculator__tile-value';
      const number = document.createElement('strong');
      number.textContent = valueText;
      value.append(number, document.createTextNode(unitText));
      item.append(label, value);
      return item;
    };

    const items = [];
    if (this.#sizing) {
      const { selectedKw, battery, modules } = this.#sizing;
      items.push(tile(s.tile?.inverter ?? '', String(selectedKw), 'kW'));
      if (battery && modules > 0) items.push(tile(s.tile?.battery ?? '', String(modules), s.pieces));
    }
    for (const [role, field] of order) {
      if (!(materials[field] > 0)) continue;
      items.push(tile((s.tile?.[role] ?? role).replace('__ROOF__', roofName), String(materials[field]), role === 'rail' ? s.pieces_bars : s.pieces));
    }
    tiles.replaceChildren(...items);
  }

  #renderList() {
    const { rows, count, summary, total } = this.refs;
    const core = this.#core;
    const s = this.#strings;
    const rootId = this.id;
    const system = this.#state.kind === 'system';

    // Re-rendering replaces the rows; put focus back on the same control of the same row.
    const active = document.activeElement;
    let restore = null;
    if (active instanceof HTMLElement && rows.contains(active)) {
      const row = active.closest('li');
      const control = active.matches('input')
        ? 'input'
        : active.dataset.step
          ? `[data-step="${active.dataset.step}"]`
          : active.matches('[data-add-line]') ? '[data-add-line]' : '';
      restore = { index: row ? [...rows.children].indexOf(row) : -1, control };
    }

    rows.replaceChildren(
      ...this.#lines.map((line, index) => {
        const entry = line.entry;
        const item = document.createElement('li');
        item.className = 'structure-calculator__row';

        const image = document.createElement('img');
        image.className = 'structure-calculator__thumb';
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        image.alt = '';
        if (entry?.image) image.src = entry.image;

        const info = document.createElement('div');
        info.className = 'structure-calculator__info';
        if (system) {
          const category = document.createElement('p');
          category.className = 'system-calculator__category';
          const kind = line.role === 'inverter' || line.role === 'battery' ? line.role : 'structure';
          category.textContent = s[`category_${kind}`] ?? '';
          info.append(category);
        }
        const name = document.createElement(entry?.url ? 'a' : 'span');
        name.className = 'structure-calculator__name';
        if (entry?.url) /** @type {HTMLAnchorElement} */ (name).href = entry.url;
        name.textContent = this.#productName(entry, line.role);
        const meta = document.createElement('p');
        meta.className = 'structure-calculator__meta';
        if (!line.purchasable) {
          meta.classList.add('structure-calculator__meta--warning');
          meta.textContent = s.unavailable;
        } else if (line.packSize > 1) {
          meta.textContent = this.#fill(s.pack_price, {
            SIZE: line.packSize,
            PRICE: core.money(entry.price, this),
            UNIT: core.money(entry.price / line.packSize, this),
          });
        } else {
          meta.textContent = this.#fill(s.unit_price, { PRICE: core.money(entry.price, this) });
        }
        info.append(name, meta);

        const inputId = `${rootId}-line-${line.entry?.key ?? index}`;
        const qty = document.createElement('div');
        qty.className = 'structure-calculator__qty calculator-stepper calculator-stepper--sm';
        qty.dataset.stepper = '';
        qty.innerHTML = `
          <label class="calculator-label" for="${inputId}"></label>
          <div class="calculator-stepper__control">
            <button type="button" class="calculator-stepper__button" data-step="-1"></button>
            <input class="calculator-stepper__input" id="${inputId}" type="number" inputmode="numeric" min="1" max="9999" step="1">
            <button type="button" class="calculator-stepper__button calculator-stepper__button--primary" data-step="1"></button>
          </div>`;
        const [minus, plus] = qty.querySelectorAll('button');
        const input = /** @type {HTMLInputElement} */ (qty.querySelector('input'));
        /** @type {HTMLElement} */ (qty.querySelector('label')).textContent = line.packSize > 1 ? s.boxes : s.quantity;
        minus.innerHTML = this.#icon('minus');
        plus.innerHTML = this.#icon('plus');
        minus.setAttribute('aria-label', this.#fill(s.decrease, { PRODUCT: name.textContent }));
        plus.setAttribute('aria-label', this.#fill(s.increase, { PRODUCT: name.textContent }));
        minus.disabled = line.quantity <= 1;
        input.value = String(line.quantity);
        input.dataset.line = String(index);

        const price = document.createElement('p');
        price.className = 'structure-calculator__line-total';
        price.textContent = line.purchasable ? core.money(core.lineTotal(line), this) : '–';

        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'structure-calculator__add';
        add.dataset.addLine = String(index);
        add.disabled = !line.purchasable;
        add.setAttribute('aria-label', this.#fill(s.add_line, { PRODUCT: name.textContent }));
        add.innerHTML = this.#icon('cart');

        item.append(image, info, qty, price, add);
        return item;
      })
    );
    if (restore?.control) {
      /** @type {HTMLElement | null | undefined} */ (rows.children[restore.index]?.querySelector(restore.control))?.focus();
    }

    const products = this.#lines.length;
    const plural = new Intl.PluralRules('ro').select(products);
    if (count) count.textContent = this.#fill(s.product_count?.[plural] ?? s.product_count?.other ?? '', { N: products });

    total.textContent = core.money(this.#sum(), this);

    if (summary) {
      const st = this.#state;
      const sizing = this.#sizing;
      const rowsList = [
        ...(sizing
          ? [
              [s.summary_system, st.system === 'hybrid' ? s.system_hybrid : s.system_ongrid],
              [s.summary_connection, st.phases === 'single' ? s.connection_single : s.connection_three],
              [s.summary_inverter, this.#fill(s.kw, { N: sizing.selectedKw })],
              [s.summary_battery, sizing.battery && sizing.modules > 0 ? this.#fill(s.battery_modules, { N: sizing.modules }) : s.no_battery],
            ]
          : []),
        [s.summary_panels, String(st.layout.reduce((n, r) => n + r.length, 0))],
        [s.summary_rows, String(st.layout.length)],
        [s.summary_orientation, st.orientation === 'landscape' ? s.orientation_landscape : s.orientation_portrait],
        st.roof === 'minirail'
          ? [s.summary_minirail, this.#structure.entries.find((e) => e.key === st.minirailKey)?.label ?? '']
          : [s.summary_size, this.#mm(st.panelLength)],
        [s.summary_roof, s.roof?.[st.roof] ?? st.roof],
        [s.summary_mode, st.custom ? s.mode_custom : s.mode_auto],
      ];
      summary.replaceChildren(
        ...rowsList.map(([term, value]) => {
          const wrapper = document.createElement('div');
          const dt = document.createElement('dt');
          const dd = document.createElement('dd');
          dt.textContent = term ?? '';
          dd.textContent = value ?? '';
          wrapper.append(dt, dd);
          return wrapper;
        })
      );
    }
  }

  #onLineQuantity = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.line === undefined) return;
    if (event.type === 'change') this.#core.clampInput(input);
    const line = this.#lines[Number(input.dataset.line)];
    if (!line?.entry) return;

    const value = Math.max(1, parseInt(input.value, 10) || 1);
    line.quantity = value;
    this.#overrides.set(line.entry.key, value);

    // Totals, payback and TBI follow once the number is settled.
    if (event.type === 'change') this.#render();
  };

  #onLineAdd = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-add-line]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const line = this.#lines[Number(button.dataset.addLine)];
    if (!line?.purchasable || !line.entry) return;
    await this.#add([line], button);
  };

  #onAddAll = async (event) => {
    const button = event.currentTarget instanceof HTMLButtonElement ? event.currentTarget : null;
    await this.#add(this.#lines.filter((line) => line.purchasable), button);
  };

  /**
   * @param {import('./admira-calculator-core.js').Line[]} lines
   * @param {HTMLButtonElement | null} button
   */
  async #add(lines, button) {
    if (!lines.length) return;
    if (button) button.disabled = true;
    const result = await this.#core.addToCart(
      this,
      lines.map((line) => ({ variantId: line.entry?.variant_id ?? 0, quantity: line.quantity }))
    );
    if (button) button.disabled = false;
    this.#status(result.ok ? this.#strings.added : result.message || this.#strings.add_error, result.ok ? 'ok' : 'error');
  }

  /* ------------------------------------------------------------------ */
  /* Payback and TBI — complete system only                             */
  /* ------------------------------------------------------------------ */

  #paybackYears(cost) {
    if (!this.#sizing) return null;
    return this.#core.paybackYears({
      costRon: cost / 100,
      kw: this.#sizing.selectedKw,
      monthlyKwh: this.#state.consumption,
      hybrid: this.#state.system === 'hybrid',
      pricePerKwh: parseFloat(this.dataset.energyPrice || '1.47'),
      increase: parseFloat(this.dataset.energyIncrease || '5') / 100,
      yieldPerKw: parseFloat(this.dataset.yield || '1350'),
    });
  }

  #references() {
    return this.#core.parseTbiReference(this.dataset.tbiReference);
  }

  #monthly(credit, term) {
    return this.#core.tbiMonthly(this.#references(), credit, term);
  }

  #eligible(credit) {
    return credit >= Number(this.dataset.tbiMin || 2000) && credit <= Number(this.dataset.tbiMax || 90000);
  }

  #renderTbi = () => {
    if (!this.#sizing) return;
    const { credit, term, rate, ratesFrom } = this.refs;
    const t = this.#strings;
    const amount = Math.round(this.#sum() / 100);

    if (credit && document.activeElement !== credit) credit.value = String(amount);
    const requested = credit ? Math.max(0, Number(credit.value) || 0) : amount;
    const months = Number(term?.value || 36);

    if (rate) rate.textContent = this.#eligible(requested) ? this.#ron(this.#monthly(requested, months)) : '–';
    if (ratesFrom) {
      const longest = Math.max(...Object.keys(this.#references()[0]?.rates ?? { 60: 0 }).map(Number));
      ratesFrom.textContent = this.#eligible(amount)
        ? this.#fill(t.rates_from, { AMOUNT: this.#ron(this.#monthly(amount, longest)) })
        : t.rates_ineligible;
    }

    // Keep the request form in step with the simulator.
    const form = this.querySelector('admira-request-form');
    const setHidden = (selector, value) => {
      const input = form?.querySelector(selector);
      if (input instanceof HTMLInputElement) input.value = value;
    };
    setHidden('[data-tbi-term]', String(months));
    setHidden('[data-tbi-rate]', this.#eligible(requested) ? this.#ron(this.#monthly(requested, months)) : '');
  };

  #renderPlan() {
    const { credit, term, planBody } = this.refs;
    if (!planBody) return;
    const requested = Number(credit?.value) || 0;
    const months = Number(term?.value || 36);
    const rows = this.#eligible(requested)
      ? this.#core.tbiPlan(this.#monthly(requested, months), months, parseFloat(this.dataset.tbiRate || '9.9'))
      : [];

    planBody.replaceChildren(
      ...rows.map((cells) => {
        const tr = document.createElement('tr');
        cells.forEach((value, index) => {
          const cell = document.createElement(index === 0 ? 'th' : 'td');
          if (index === 0) cell.setAttribute('scope', 'row');
          cell.textContent = index === 0 ? String(value) : this.#ron(value);
          tr.append(cell);
        });
        return tr;
      })
    );
  }

  #prefillFinance() {
    const form = this.querySelector('admira-request-form form');
    const sizing = this.#sizing;
    if (!form || !sizing) return;
    const t = this.#strings;
    const s = this.#state;
    const credit = form.querySelector('input[name="contact[Valoare credit RON]"]');
    if (credit instanceof HTMLInputElement && document.activeElement !== credit) credit.value = String(Math.round(this.#sum() / 100));

    const details = form.querySelector('textarea[name="contact[body]"]');
    if (details instanceof HTMLTextAreaElement && !details.dataset.edited) {
      const modules = sizing.battery ? sizing.modules : 0;
      details.value = this.#fill(t.finance_details, {
        SYSTEM: s.system === 'hybrid' ? t.system_hybrid : t.system_ongrid,
        KW: sizing.selectedKw,
        BATTERY: modules ? this.#fill(t.finance_battery, { N: modules }) : '',
        STRUCTURE: this.#structureName(),
      }).replace(/\s+/g, ' ').trim();
      if (!details.dataset.watched) {
        details.dataset.watched = 'true';
        details.addEventListener('input', () => (details.dataset.edited = 'true'), { once: true });
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */

  #structureName() {
    const t = this.#strings;
    const s = this.#state;
    return this.#fill(t.structure_name, {
      ROOF: (t.roof?.[s.roof] ?? s.roof).toLowerCase(),
      PANELS: s.layout.reduce((n, r) => n + r.length, 0),
      ROWS: s.layout.length,
      ORIENTATION: (s.orientation === 'landscape' ? t.orientation_landscape : t.orientation_portrait).toLowerCase(),
    });
  }

  #productName(entry, role) {
    if (!entry) return this.#strings.tile?.[role] ?? role;
    if (!entry.title) return entry.label || entry.key;
    const variant = entry.variant_title && entry.variant_title !== 'Default Title' ? ` – ${entry.variant_title}` : '';
    return `${entry.title}${variant}`;
  }

  #icon(name) {
    const template = this.querySelector(`template[data-icon="${name}"]`);
    return template instanceof HTMLTemplateElement ? template.innerHTML : '';
  }

  /** @param {number} value - lei, two decimals, Romanian separators */
  #ron(value) {
    return value.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  #mm(value) {
    return this.#fill(this.#strings.mm ?? '__N__ mm', { N: value });
  }

  #t(key, values) {
    return this.#fill(this.#strings[key] ?? '', values);
  }

  #fill(template, values) {
    return Object.entries(values ?? {}).reduce(
      (text, [name, value]) => text.replaceAll(`__${name}__`, String(value)),
      String(template ?? '')
    );
  }

  #announce(message) {
    const { announcer } = this.refs;
    if (!announcer) return;
    announcer.textContent = '';
    requestAnimationFrame(() => (announcer.textContent = message));
  }

  #status(message, state) {
    const { status } = this.refs;
    if (!status) return;
    status.textContent = message;
    status.dataset.state = state;
  }
}

if (!customElements.get('admira-solar-calculator')) {
  customElements.define('admira-solar-calculator', AdmiraSolarCalculator);
}
